import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllTeachersOptions,
	getAllTeachersQueryKey,
	createTeacherMutation,
	updateTeacherMutation,
	deleteTeacherMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Eye, AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import type { TeacherDto } from "@/lib/client/types.gen";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	DataTableFilterText,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";

export const Route = createFileRoute("/admin/teachers/")({
	component: TeachersManagementPage,
});

function TeachersManagementPage() {
	const queryClient = useQueryClient();
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedTeacher, setSelectedTeacher] = useState<TeacherDto | null>(
		null,
	);
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
	});

	const { data: teachers, isLoading } = useQuery(getAllTeachersOptions());

	const createTeacher = useMutation({
		...createTeacherMutation(),
		onSuccess: () => {
			toast.success("Professeur créé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllTeachersQueryKey() });
			setCreateDialogOpen(false);
			setFormData({ firstName: "", lastName: "" });
		},
		onError: () => {
			toast.error("Erreur lors de la création du professeur");
		},
	});

	const updateTeacher = useMutation({
		...updateTeacherMutation(),
		onSuccess: () => {
			toast.success("Professeur modifié avec succès");
			queryClient.invalidateQueries({ queryKey: getAllTeachersQueryKey() });
			setEditDialogOpen(false);
			setSelectedTeacher(null);
			setFormData({ firstName: "", lastName: "" });
		},
		onError: () => {
			toast.error("Erreur lors de la modification du professeur");
		},
	});

	const deleteTeacher = useMutation({
		...deleteTeacherMutation(),
		onSuccess: () => {
			toast.success("Professeur supprimé avec succès");
			queryClient.invalidateQueries({ queryKey: getAllTeachersQueryKey() });
			setDeleteDialogOpen(false);
			setSelectedTeacher(null);
		},
		onError: () => {
			toast.error("Erreur lors de la suppression du professeur");
		},
	});

	const handleCreate = () => {
		createTeacher.mutate({
			body: formData,
		});
	};

	const handleUpdate = () => {
		if (!selectedTeacher) return;
		updateTeacher.mutate({
			path: { id: selectedTeacher.id },
			body: formData,
		});
	};

	const handleDelete = () => {
		if (!selectedTeacher) return;
		deleteTeacher.mutate({
			path: { id: selectedTeacher.id },
		});
	};

	const openEditDialog = (teacher: TeacherDto) => {
		setSelectedTeacher(teacher);
		setFormData({
			firstName: teacher.firstName,
			lastName: teacher.lastName,
		});
		setEditDialogOpen(true);
	};

	const openDeleteDialog = (teacher: TeacherDto) => {
		setSelectedTeacher(teacher);
		setDeleteDialogOpen(true);
	};

	const columns = useMemo<ColumnDef<TeacherDto>[]>(
		() => [
			{
				accessorKey: "firstName",
				meta: { label: "Prénom" },
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Prénom" />
				),
				cell: ({ row }) => row.getValue("firstName"),
			},
			{
				accessorKey: "lastName",
				meta: { label: "Nom" },
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Nom" />
				),
				cell: ({ row }) => row.getValue("lastName"),
			},
			{
				id: "fullName",
				accessorFn: (row) => row.lastName + " " + row.firstName,
				meta: { label: "Nom complet" },
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Nom complet" />
				),
				cell: ({ row }) => (
					<span>
						{row.original.firstName} {row.original.lastName}
					</span>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					return fullName.includes(searchValue) || reverseName.includes(searchValue);
				},
				sortDescFirst: false,
			},
			{
				id: "actions",
				meta: { label: "Actions" },
				enableSorting: false,
				enableHiding: false,
				cell: ({ row }) => {
					const teacher = row.original;
					return (
						<div className="flex justify-end gap-2">
                            <Link
                                to="/admin/teachers/$id"
                                params={{ id: teacher.id }}
                            >
                                <Button
                                    variant="outline"
                                    size="sm"
                                    title="Voir le détail"
                                >
                                    <Eye className="h-4 w-4" />
                                </Button>
                            </Link>
							<Button
								variant="outline"
								size="sm"
								onClick={() => openEditDialog(teacher)}
							>
								<Pencil className="h-4 w-4" />
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => openDeleteDialog(teacher)}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>
					);
				},
			},
		],
		[],
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-96">
				<p>Chargement...</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<div>
					<h1 className="text-3xl font-bold">Gestion des Professeurs</h1>
					<p className="text-gray-600 mt-2">
						Gérer les professeurs du système
					</p>
				</div>
			</div>
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>Liste des professeurs</CardTitle>
						</div>
						<Button onClick={() => setCreateDialogOpen(true)}>
							<UserPlus className="mr-2 h-4 w-4" />
							Nouveau Professeur
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={teachers || []}
						emptyMessage="Aucun professeur"
						toolbar={(table) => (
							<DataTableToolbar table={table} showExportExcel exportFilename="professeurs">
								<DataTableFilterText
									column={table.getColumn("fullName")!}
									placeholder="Rechercher un professeur..."
								/>
							</DataTableToolbar>
						)}
					/>
				</CardContent>
			</Card>

			<Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Créer un nouveau professeur</DialogTitle>
						<DialogDescription>
							Remplissez les informations du professeur
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label htmlFor="firstName">Prénom</Label>
							<Input
								id="firstName"
								value={formData.firstName}
								onChange={(e) =>
									setFormData({ ...formData, firstName: e.target.value })
								}
								placeholder="Jean"
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="lastName">Nom</Label>
							<Input
								id="lastName"
								value={formData.lastName}
								onChange={(e) =>
									setFormData({ ...formData, lastName: e.target.value })
								}
								placeholder="Dupont"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setCreateDialogOpen(false)}
						>
							Annuler
						</Button>
						<Button onClick={handleCreate}>Créer</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Modifier le professeur</DialogTitle>
						<DialogDescription>
							Modifiez les informations du professeur
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label htmlFor="edit-firstName">Prénom</Label>
							<Input
								id="edit-firstName"
								value={formData.firstName}
								onChange={(e) =>
									setFormData({ ...formData, firstName: e.target.value })
								}
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="edit-lastName">Nom</Label>
							<Input
								id="edit-lastName"
								value={formData.lastName}
								onChange={(e) =>
									setFormData({ ...formData, lastName: e.target.value })
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditDialogOpen(false)}>
							Annuler
						</Button>
						<Button onClick={handleUpdate}>Modifier</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Supprimer le professeur</DialogTitle>
						<DialogDescription>
							{selectedTeacher && selectedTeacher.activeCourseCount > 0 ? (
								<span className="flex items-start gap-2 mt-2">
									<AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
									<span>
										Impossible de supprimer <strong>{selectedTeacher.fullName}</strong> car il est encore rattaché à{" "}
										<strong>{selectedTeacher.activeCourseCount} cours</strong>.
										Veuillez d'abord réassigner ou supprimer ces cours avant de pouvoir supprimer ce professeur.
									</span>
								</span>
							) : (
								<>
									Êtes-vous sûr de vouloir supprimer {selectedTeacher?.fullName} ?
									Cette action est irréversible.
								</>
							)}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setDeleteDialogOpen(false)}
						>
							{selectedTeacher && selectedTeacher.activeCourseCount > 0 ? "Fermer" : "Annuler"}
						</Button>
						{selectedTeacher && selectedTeacher.activeCourseCount === 0 && (
							<Button variant="destructive" onClick={handleDelete}>
								Supprimer
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
